import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import { Plus, Edit, Trash2, BookOpen, FolderOpen } from 'lucide-react';

const apiUrl = config.apiUrl;

const CreateModule = () => {
  const [modules, setModules] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  
  const [moduleForm, setModuleForm] = useState({
    subject_id: '',
    name: '',
    description: '',
    order: 0
  });
  
  const [lessonForm, setLessonForm] = useState({
    module_id: '',
    title: '',
    content: '',
    order: 0,
    revision_number: 0
  });
  
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('modules');

  // Fetch all data
  useEffect(() => {
    fetchModules();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (activeModule) {
      fetchLessons(activeModule.id);
    }
  }, [activeModule]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${apiUrl}/modules`);
      setModules(response.data.data || response.data );
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${apiUrl}/subjects`);
      setSubjects(response.data);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  };

  const fetchLessons = async (moduleId) => {
    try {
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`);
      setLessons(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching lessons:', err);
    }
  };

  const handleModuleChange = (e) => {
    const { name, value } = e.target;
    setModuleForm(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) : value
    }));
  };

  const handleLessonChange = (e) => {
    const { name, value } = e.target;
    setLessonForm(prev => ({
      ...prev,
      [name]: name === 'order' || name === 'revision_number' ? parseInt(value) : value
    }));
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingModule) {
        await axios.put(`${apiUrl}/modules/${editingModule.id}`, moduleForm);
        alert('Module updated successfully!');
      } else {
        await axios.post(`${apiUrl}/modules`, moduleForm);
        alert('Module created successfully!');
      }
      
      fetchModules();
      resetModuleForm();
    } catch (err) {
      console.error('Error saving module:', err);
      setError(err.response?.data?.message || 'Failed to save module. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLessonSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingLesson) {
        await axios.put(`${apiUrl}/lessons/${editingLesson.id}`, lessonForm);
        alert('Lesson updated successfully!');
      } else {
        await axios.post(`${apiUrl}/lessons`, lessonForm);
        alert('Lesson created successfully!');
      }
      
      if (activeModule) {
        fetchLessons(activeModule.id);
      }
      resetLessonForm();
    } catch (err) {
      console.error('Error saving lesson:', err);
      setError(err.response?.data?.message || 'Failed to save lesson. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      subject_id: module.subject_id,
      name: module.name,
      description: module.description || '',
      order: module.order || 0
    });
    setActiveTab('moduleForm');
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      module_id: lesson.module_id,
      title: lesson.title,
      content: lesson.content || '',
      order: lesson.order || 0,
      revision_number: lesson.revision_number || 0
    });
    setActiveTab('lessonForm');
  };

  const handleDeleteModule = async (id) => {
    if (window.confirm('Are you sure you want to delete this module? This will also delete all lessons associated with it.')) {
      try {
        await axios.delete(`${apiUrl}/modules/${id}`);
        alert('Module deleted successfully!');
        fetchModules();
        if (activeModule && activeModule.id === id) {
          setActiveModule(null);
          setLessons([]);
        }
      } catch (err) {
        console.error('Error deleting module:', err);
        setError('Failed to delete module');
      }
    }
  };

  const handleDeleteLesson = async (id) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      try {
        await axios.delete(`${apiUrl}/lessons/${id}`);
        alert('Lesson deleted successfully!');
        if (activeModule) {
          fetchLessons(activeModule.id);
        }
      } catch (err) {
        console.error('Error deleting lesson:', err);
        setError('Failed to delete lesson');
      }
    }
  };

  const resetModuleForm = () => {
    setModuleForm({
      subject_id: '',
      name: '',
      description: '',
      order: 0
    });
    setEditingModule(null);
  };

  const resetLessonForm = () => {
    setLessonForm({
      module_id: activeModule?.id || '',
      title: '',
      content: '',
      order: 0,
      revision_number: 0
    });
    setEditingLesson(null);
  };

  const selectModule = (module) => {
    setActiveModule(module);
    setLessonForm(prev => ({ ...prev, module_id: module.id }));
    setActiveTab('lessons');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading modules...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Module & Lesson Management</h1>
      
      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Modules List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Modules</h2>
            <button
              onClick={() => {
                resetModuleForm();
                setActiveTab('moduleForm');
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            >
              <Plus size={16} className="mr-1" /> New
            </button>
          </div>
          
          {modules.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No modules found. Create your first module!
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {modules.map(module => (
                <div
                  key={module.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    activeModule?.id === module.id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  onClick={() => selectModule(module)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{module.name}</h3>
                      <p className="text-sm text-gray-600">{module.subject?.name}</p>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditModule(module);
                        }}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Edit module"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteModule(module.id);
                        }}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete module"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {module.description && (
                    <p className="text-sm text-gray-500 mt-1">{module.description}</p>
                  )}
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">Order: {module.order}</span>
                    <span className="text-xs text-green-600">
                      {module.lessons?.length || 0} lessons
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Middle Column - Module Form or Lessons List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {activeTab === 'moduleForm' && (
            <>
              <h2 className="text-2xl font-bold mb-6 text-center">
                {editingModule ? 'Edit Module' : 'Create New Module'}
              </h2>
              
              <form onSubmit={handleModuleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-1">Subject*</label>
                  <select
                    name="subject_id"
                    value={moduleForm.subject_id}
                    onChange={handleModuleChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Module Name*</label>
                  <input
                    type="text"
                    name="name"
                    value={moduleForm.name}
                    onChange={handleModuleChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="Enter module name"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={moduleForm.description}
                    onChange={handleModuleChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[80px]"
                    placeholder="Module description (optional)"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Order</label>
                  <input
                    type="number"
                    name="order"
                    value={moduleForm.order}
                    onChange={handleModuleChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setActiveTab('modules')}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : editingModule ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </>
          )}

          {activeTab === 'lessons' && activeModule && (
            <>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Lessons in {activeModule.name}</h2>
                  <p className="text-gray-600">{activeModule.subject?.name}</p>
                </div>
                <button
                  onClick={() => {
                    resetLessonForm();
                    setActiveTab('lessonForm');
                  }}
                  className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                >
                  <Plus size={16} className="mr-1" /> New Lesson
                </button>
              </div>
              
              {lessons.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No lessons found in this module. Create your first lesson!
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {lessons.map(lesson => (
                    <div key={lesson.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{lesson.title}</h3>
                          <p className="text-sm text-gray-600">Order: {lesson.order}</p>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditLesson(lesson)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Edit lesson"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteLesson(lesson.id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Delete lesson"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      {lesson.content && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {lesson.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'lessonForm' && (
            <>
              <h2 className="text-2xl font-bold mb-6 text-center">
                {editingLesson ? 'Edit Lesson' : 'Create New Lesson'}
              </h2>
              {activeModule && (
                <p className="text-gray-600 mb-4 text-center">
                  Module: {activeModule.name}
                </p>
              )}
              
              <form onSubmit={handleLessonSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-1">Lesson Title*</label>
                  <input
                    type="text"
                    name="title"
                    value={lessonForm.title}
                    onChange={handleLessonChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder="Enter lesson title"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1">Content</label>
                  <textarea
                    name="content"
                    value={lessonForm.content}
                    onChange={handleLessonChange}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                    placeholder="Lesson content (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-1">Order</label>
                    <input
                      type="number"
                      name="order"
                      value={lessonForm.order}
                      onChange={handleLessonChange}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1">Revision Number</label>
                    <input
                      type="number"
                      name="revision_number"
                      value={lessonForm.revision_number}
                      onChange={handleLessonChange}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setActiveTab('lessons')}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : editingLesson ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Right Column - Module Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-6">Information</h2>
          
          {activeModule ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <FolderOpen className="text-blue-600" size={24} />
                <div>
                  <h3 className="font-semibold">{activeModule.name}</h3>
                  <p className="text-sm text-gray-600">{activeModule.subject?.name}</p>
                </div>
              </div>
              
              {activeModule.description && (
                <div>
                  <h4 className="font-medium text-gray-700">Description:</h4>
                  <p className="text-sm text-gray-600">{activeModule.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-600">Order</p>
                  <p className="font-semibold">{activeModule.order}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-600">Total Lessons</p>
                  <p className="font-semibold">{lessons.length}</p>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setActiveModule(null);
                  setActiveTab('modules');
                }}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Back to Modules
              </button>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <BookOpen className="mx-auto mb-4 text-gray-400" size={48} />
              <p>Select a module to view details and manage lessons</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateModule;